import { useLayoutEffect, useRef } from 'react';
import { useSiteContent } from '../context/SiteContentContext';

const PHOSPHOR_CROWN_FILL =
  'M248,80a28,28,0,1,0-51.82,14.25l-26.52,41.11L146,93.64a28,28,0,1,0-36,0L86.34,135.36,59.82,94.25a28,28,0,1,0-26.6,12L48,200a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16l14.78-93.79A28,28,0,0,0,248,80Z';

const SEP = '•';
const PX_PER_SEC = 72;

function BannerCrown({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PHOSPHOR_CROWN_FILL} />
    </svg>
  );
}

function navigateBannerLink(href) {
  const raw = String(href || '').trim();
  if (!raw) return;
  if (/^https?:\/\//i.test(raw)) {
    window.location.assign(raw);
    return;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  const url = new URL(path, window.location.origin);
  window.history.pushState({}, '', `${url.pathname}${url.search}`);
  window.dispatchEvent(new Event('vb-route-change'));
}

function normalizeTexts(banner) {
  if (!banner) return [];
  const fromArr = Array.isArray(banner.texts)
    ? banner.texts.map((t) => String(t || '').trim()).filter(Boolean)
    : [];
  if (fromArr.length) return fromArr;
  const single = String(banner.text || '').trim();
  return single ? [single] : [];
}

function buildLine(texts) {
  return texts.map((t) => String(t).trim()).filter(Boolean).join(` ${SEP} `);
}

export default function NotificationBanner() {
  const { notificationBanner, loading } = useSiteContent();
  const viewportRef = useRef(null);
  const textRef = useRef(null);

  let texts = [];
  let linkUrl = '';
  let buttonText = 'Detaylar';
  if (!loading && notificationBanner?.enabled) {
    texts = normalizeTexts(notificationBanner);
    linkUrl = String(notificationBanner.linkUrl || '').trim();
    buttonText = String(notificationBanner.buttonText || '').trim() || 'Detaylar';
  }

  const line = buildLine(texts);

  useLayoutEffect(() => {
    if (!line) return undefined;

    const viewport = viewportRef.current;
    const textEl = textRef.current;
    if (!viewport || !textEl) return undefined;

    const apply = () => {
      const viewW = Math.max(1, Math.round(viewport.clientWidth));
      const textW = Math.max(1, Math.round(textEl.getBoundingClientRect().width));
      const start = viewW;
      const end = -textW;
      const distance = start - end;
      const duration = Math.max(16, Math.min(56, distance / PX_PER_SEC));
      textEl.style.setProperty('--nb-start', `${start}px`);
      textEl.style.setProperty('--nb-end', `${end}px`);
      textEl.style.setProperty('--nb-duration', `${duration}s`);
      textEl.style.animation = 'none';
      void textEl.offsetWidth;
      textEl.style.animation = '';
    };

    apply();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) {
      ro.observe(viewport);
      ro.observe(textEl);
    }

    const onResize = () => apply();
    window.addEventListener('resize', onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(apply).catch(() => {});
    }

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [line]);

  if (!line) return null;

  return (
    <div className="notification-banner" role="status" aria-live="polite">
      <span className="notification-banner__icon" aria-hidden="true">
        <BannerCrown className="notification-banner__crown" />
      </span>
      <div className="notification-banner__viewport" ref={viewportRef}>
        <span className="notification-banner__fade notification-banner__fade--left" aria-hidden="true" />
        <span className="notification-banner__fade notification-banner__fade--right" aria-hidden="true" />
        <span className="notification-banner__text" ref={textRef}>
          {line}
        </span>
      </div>
      {linkUrl ? (
        <a
          className="notification-banner__btn"
          href={linkUrl}
          onClick={(e) => {
            e.preventDefault();
            navigateBannerLink(linkUrl);
          }}
        >
          {buttonText}
        </a>
      ) : null}
    </div>
  );
}
